import { useState, useEffect, useRef, useCallback } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { Heart, MessageCircle, Share2, X, Volume2, VolumeX, ChevronUp, ChevronDown, Send, ShoppingBag, MapPin, Car, Newspaper, Check } from 'lucide-react'
import { useLanguageStore, useAuthStore } from '../../lib/store'

interface Short {
    id: number
    video_url: string
    thumbnail_url: string
    title: string
    description?: string
    views: number
    likes: number
    linked_item_type?: string
    linked_item_id?: number
    linked_item?: any // Fetched linked item data
    commentsCount?: number
}

interface Comment {
    id: number
    guest_name: string
    content: string
    created_at: string
}

export default function ShortsPage() {
    const { language } = useLanguageStore()
    const { user } = useAuthStore()
    const { videoId } = useParams<{ videoId?: string }>()

    const [shorts, setShorts] = useState<Short[]>([])
    const [currentIndex, setCurrentIndex] = useState(0)
    const [loading, setLoading] = useState(true)
    const [loadingMore, setLoadingMore] = useState(false)
    const [hasMore, setHasMore] = useState(true)
    const [page, setPage] = useState(1)
    const [muted, setMuted] = useState(true)
    const [liked, setLiked] = useState<Set<number>>(new Set())
    const [showComments, setShowComments] = useState(false)
    const [comments, setComments] = useState<Comment[]>([])
    const [commentText, setCommentText] = useState('')
    const [submittingComment, setSubmittingComment] = useState(false)
    const [videoCommentsCount, setVideoCommentsCount] = useState<Record<number, number>>({})
    const [showOverlay, setShowOverlay] = useState(true)
    const [linkedItemsCache, setLinkedItemsCache] = useState<Record<string, { name: string; image: string; price?: string }>>({})
    const [shareCopied, setShareCopied] = useState(false)
    const [swipeOffset, setSwipeOffset] = useState(0)
    const [isAnimating, setIsAnimating] = useState(false)

    const containerRef = useRef<HTMLDivElement>(null)
    const touchStartY = useRef(0)
    const touchEndY = useRef(0)
    const isSwiping = useRef(false)
    const overlayTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    // Auto-hide overlay after 3 seconds
    const resetOverlayTimer = useCallback(() => {
        if (overlayTimerRef.current) {
            clearTimeout(overlayTimerRef.current)
        }
        setShowOverlay(true)
        overlayTimerRef.current = setTimeout(() => {
            setShowOverlay(false)
        }, 3000)
    }, [])

    // Toggle overlay visibility on tap
    const handleOverlayToggle = () => {
        if (showOverlay) {
            setShowOverlay(false)
            if (overlayTimerRef.current) {
                clearTimeout(overlayTimerRef.current)
            }
        } else {
            resetOverlayTimer()
        }
    }

    // Reset timer when video changes
    useEffect(() => {
        resetOverlayTimer()
        return () => {
            if (overlayTimerRef.current) {
                clearTimeout(overlayTimerRef.current)
            }
        }
    }, [currentIndex, resetOverlayTimer])

    // Fetch shorts
    const fetchShorts = useCallback(async (pageNum: number, append = false) => {
        if (append) setLoadingMore(true)
        else setLoading(true)

        try {
            const res = await fetch(`/api/shorts?page=${pageNum}&limit=10&lang=${language}`)
            const data = await res.json()

            if (data.success && data.data) {
                if (append) {
                    setShorts(prev => [...prev, ...data.data])
                } else {
                    setShorts(data.data)
                }
                setHasMore(data.hasMore)
            }
        } catch (error) {
            console.error('Failed to fetch shorts:', error)
        } finally {
            setLoading(false)
            setLoadingMore(false)
        }
    }, [language])

    useEffect(() => {
        fetchShorts(1)
    }, [fetchShorts])

    // Navigate to specific video ID from URL
    useEffect(() => {
        if (videoId && shorts.length > 0) {
            const videoIndex = shorts.findIndex(s => s.id === parseInt(videoId))
            if (videoIndex !== -1 && videoIndex !== currentIndex) {
                setCurrentIndex(videoIndex)
            }
        }
    }, [videoId, shorts])

    // Update URL when video changes (without full navigation)
    useEffect(() => {
        const video = shorts[currentIndex]
        if (video && !loading) {
            const newUrl = `/shorts/${video.id}`
            if (window.location.pathname !== newUrl) {
                window.history.replaceState(null, '', newUrl)
            }
        }
    }, [currentIndex, shorts, loading])

    // Load more when reaching near end
    useEffect(() => {
        if (currentIndex >= shorts.length - 3 && hasMore && !loadingMore) {
            const nextPage = page + 1
            setPage(nextPage)
            fetchShorts(nextPage, true)
        }
    }, [currentIndex, shorts.length, hasMore, loadingMore, page, fetchShorts])

    // Fetch video details (comments count) when video changes
    useEffect(() => {
        const video = shorts[currentIndex]
        if (video && videoCommentsCount[video.id] === undefined) {
            fetchVideoDetails(video.id)
        }
    }, [currentIndex, shorts])

    const fetchVideoDetails = async (videoId: number) => {
        try {
            const res = await fetch(`/api/shorts/${videoId}/comments`)
            const data = await res.json()
            if (data.success) {
                setVideoCommentsCount(prev => ({ ...prev, [videoId]: data.comments?.length || 0 }))
            }
        } catch (error) {
            console.error('Failed to fetch video details:', error)
        }
    }

    // Fetch linked item details when video changes
    useEffect(() => {
        const video = shorts[currentIndex]
        if (video?.linked_item_type && video.linked_item_type !== 'none' && video.linked_item_id) {
            const cacheKey = `${video.linked_item_type}-${video.linked_item_id}`
            if (!linkedItemsCache[cacheKey]) {
                fetchLinkedItemDetails(video.linked_item_type, video.linked_item_id)
            }
        }
    }, [currentIndex, shorts])

    const fetchLinkedItemDetails = async (type: string, id: number) => {
        const cacheKey = `${type}-${id}`

        try {
            let endpoint = ''
            switch (type) {
                case 'tour':
                    endpoint = `/api/tours/${id}`
                    break
                case 'vehicle':
                    endpoint = `/api/vehicles/${id}`
                    break
                case 'article':
                    endpoint = `/api/news/id/${id}`
                    break
            }

            if (endpoint) {
                const res = await fetch(endpoint)
                const data = await res.json()

                if (data.success || data.data) {
                    const item = data.data || data
                    setLinkedItemsCache(prev => ({
                        ...prev,
                        [cacheKey]: {
                            name: item.name_en || item.title_en || item.name || item.title || `#${id}`,
                            image: item.images?.[0] || item.featured_image || item.image || '',
                            price: item.price ? `${item.price.toLocaleString()} VND` : undefined
                        }
                    }))
                }
            }
        } catch (error) {
            console.error('Failed to fetch linked item:', error)
        }
    }
    useEffect(() => {
        if (showComments && shorts[currentIndex]) {
            fetchComments(shorts[currentIndex].id)
        }
    }, [showComments, currentIndex, shorts])

    const fetchComments = async (videoId: number) => {
        try {
            const res = await fetch(`/api/shorts/${videoId}/comments`)
            const data = await res.json()
            if (data.success) {
                setComments(data.comments || [])
                setVideoCommentsCount(prev => ({ ...prev, [videoId]: data.comments?.length || 0 }))
            }
        } catch (error) {
            console.error('Failed to fetch comments:', error)
        }
    }

    const handleLike = async () => {
        const video = shorts[currentIndex]
        if (!video) return

        try {
            const res = await fetch(`/api/shorts/${video.id}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'like', user_id: user?.id })
            })
            const data = await res.json()

            if (data.success) {
                const newLiked = new Set(liked)
                if (data.liked) {
                    newLiked.add(video.id)
                    setShorts(prev => prev.map(s => s.id === video.id ? { ...s, likes: s.likes + 1 } : s))
                } else {
                    newLiked.delete(video.id)
                    setShorts(prev => prev.map(s => s.id === video.id ? { ...s, likes: Math.max(0, s.likes - 1) } : s))
                }
                setLiked(newLiked)
            }
        } catch (error) {
            console.error('Like error:', error)
        }
    }

    const submitComment = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!commentText.trim() || submittingComment) return

        const video = shorts[currentIndex]
        if (!video) return

        setSubmittingComment(true)
        try {
            const res = await fetch(`/api/shorts/${video.id}/comments`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    content: commentText,
                    guest_name: user?.fullName || 'Guest',
                    user_id: user?.id
                })
            })
            const data = await res.json()
            if (data.success) {
                setCommentText('')
                fetchComments(video.id)
            }
        } catch (error) {
            console.error('Comment error:', error)
        } finally {
            setSubmittingComment(false)
        }
    }

    const handleShare = async () => {
        const video = shorts[currentIndex]
        if (!video) return

        const shareUrl = `${window.location.origin}/shorts/${video.id}`
        const shareData = {
            title: video.title || 'Phú Quốc Video',
            text: video.description || video.title || 'Check out this video from Phú Quốc!',
            url: shareUrl
        }

        try {
            // Use native share on mobile
            if (navigator.share && /Android|iPhone|iPad/i.test(navigator.userAgent)) {
                await navigator.share(shareData)
            } else {
                // Copy to clipboard on desktop
                await navigator.clipboard.writeText(shareUrl)
                setShareCopied(true)
                setTimeout(() => setShareCopied(false), 2000)
            }
        } catch (error) {
            // Fallback: copy to clipboard
            try {
                await navigator.clipboard.writeText(shareUrl)
                setShareCopied(true)
                setTimeout(() => setShareCopied(false), 2000)
            } catch {
                console.error('Share failed:', error)
            }
        }
    }

    const goNext = useCallback(() => {
        if (currentIndex < shorts.length - 1) {
            setCurrentIndex(prev => prev + 1)
            setShowComments(false)
        }
    }, [currentIndex, shorts.length])

    const goPrev = useCallback(() => {
        if (currentIndex > 0) {
            setCurrentIndex(prev => prev - 1)
            setShowComments(false)
        }
    }, [currentIndex])

    // Touch handlers for mobile swipe with animation
    const handleTouchStart = (e: React.TouchEvent) => {
        if (isAnimating) return
        touchStartY.current = e.touches[0].clientY
        isSwiping.current = false
    }

    const handleTouchMove = (e: React.TouchEvent) => {
        if (isAnimating) return
        touchEndY.current = e.touches[0].clientY
        const diff = touchStartY.current - touchEndY.current

        // Mark as swiping if movement exceeds threshold
        if (Math.abs(diff) > 10) {
            isSwiping.current = true
            // Prevent browser pull-to-refresh
            e.preventDefault()
            // Live swipe offset for visual feedback (clamp to reasonable range)
            setSwipeOffset(Math.max(-150, Math.min(150, diff)))
        }
    }

    const handleTouchEnd = () => {
        if (!isSwiping.current || isAnimating) {
            setSwipeOffset(0)
            return
        }

        const diff = touchStartY.current - touchEndY.current
        const threshold = 50

        if (Math.abs(diff) > threshold) {
            const direction = diff > 0 ? 1 : -1
            const containerHeight = window.innerHeight

            // Animate full slide
            setIsAnimating(true)
            setSwipeOffset(direction * containerHeight)

            setTimeout(() => {
                if (direction > 0) {
                    goNext()
                } else {
                    goPrev()
                }
                setSwipeOffset(0)
                setIsAnimating(false)
            }, 200)
        } else {
            // Snap back
            setSwipeOffset(0)
        }
        isSwiping.current = false
    }

    // Wheel handler for desktop scroll
    const handleWheel = (e: React.WheelEvent) => {
        e.preventDefault()
        if (Math.abs(e.deltaY) > 30) {
            if (e.deltaY > 0) {
                goNext()
            } else {
                goPrev()
            }
        }
    }

    const formatNumber = (num: number) => {
        if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M'
        if (num >= 1000) return (num / 1000).toFixed(1) + 'K'
        return num?.toString() || '0'
    }

    // Convert YouTube URL to embed URL
    const getYoutubeEmbedUrl = (url: string) => {
        const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&]+)/)
        if (match) {
            return `https://www.youtube.com/embed/${match[1]}?autoplay=1&mute=${muted ? 1 : 0}&loop=1&playlist=${match[1]}&controls=0&modestbranding=1&playsinline=1`
        }
        return url
    }

    const isYoutubeUrl = (url: string) => {
        return url.includes('youtube.com') || url.includes('youtu.be')
    }

    // Get linked item link and display
    const getLinkedItemInfo = (video: Short) => {
        if (!video.linked_item_type || video.linked_item_type === 'none') return null

        switch (video.linked_item_type) {
            case 'tour':
                return {
                    icon: MapPin,
                    label: language === 'en' ? 'View Tour' : 'Xem Tour',
                    link: `/tours/${video.linked_item_id}`,
                    color: 'bg-green-500'
                }
            case 'vehicle':
                return {
                    icon: Car,
                    label: language === 'en' ? 'View Vehicle' : 'Xem Xe',
                    link: `/vehicles/${video.linked_item_id}`,
                    color: 'bg-blue-500'
                }
            case 'article':
                return {
                    icon: Newspaper,
                    label: language === 'en' ? 'Read Article' : 'Đọc Bài viết',
                    link: `/blog/article/${video.linked_item_id}`,
                    color: 'bg-purple-500'
                }
            default:
                return null
        }
    }

    const currentShort = shorts[currentIndex]
    const linkedItem = currentShort ? getLinkedItemInfo(currentShort) : null
    const linkedItemDetails = currentShort?.linked_item_type && currentShort.linked_item_id
        ? linkedItemsCache[`${currentShort.linked_item_type}-${currentShort.linked_item_id}`]
        : null

    if (loading) {
        return (
            <div className="min-h-screen bg-black flex items-center justify-center">
                <div className="animate-pulse text-white">
                    {language === 'en' ? 'Loading...' : 'Đang tải...'}
                </div>
            </div>
        )
    }

    if (shorts.length === 0) {
        return (
            <div className="min-h-screen bg-black flex items-center justify-center text-white">
                <p>{language === 'en' ? 'No videos available' : 'Chưa có video'}</p>
            </div>
        )
    }

    return (
        <div className="min-h-screen bg-black text-white overscroll-none">
            {/* SEO Meta Tags */}
            <Helmet>
                <title>{currentShort?.title || 'Videos'} | Phú Quốc Explorer</title>
                <meta name="description" content={currentShort?.description || currentShort?.title || 'Watch amazing videos from Phú Quốc'} />
                <meta property="og:title" content={currentShort?.title || 'Phú Quốc Videos'} />
                <meta property="og:description" content={currentShort?.description || 'Watch amazing videos from Phú Quốc Island'} />
                <meta property="og:type" content="video.other" />
                <meta property="og:url" content={`https://phuquoc-rental.pages.dev/shorts/${currentShort?.id}`} />
                <meta property="og:image" content={currentShort?.thumbnail_url || `https://i.ytimg.com/vi/${currentShort?.video_url?.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&]+)/)?.[1]}/hqdefault.jpg`} />
                <meta property="og:video" content={currentShort?.video_url} />
                <link rel="canonical" href={`https://phuquoc-rental.pages.dev/shorts/${currentShort?.id}`} />
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "VideoObject",
                        "name": currentShort?.title || "Video",
                        "description": currentShort?.description || currentShort?.title || "Watch this video from Phú Quốc",
                        "thumbnailUrl": currentShort?.thumbnail_url,
                        "uploadDate": new Date().toISOString(),
                        "contentUrl": currentShort?.video_url,
                        "interactionStatistic": {
                            "@type": "InteractionCounter",
                            "interactionType": "https://schema.org/WatchAction",
                            "userInteractionCount": currentShort?.views || 0
                        }
                    })}
                </script>
            </Helmet>

            {/* Full Screen Video Container */}
            <div
                ref={containerRef}
                className="fixed inset-0 flex items-center justify-center touch-none select-none overflow-hidden"
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                onWheel={handleWheel}
                style={{ touchAction: 'none', overscrollBehavior: 'none' }}
            >
                {/* Video Container with Swipe Animation */}
                <div
                    className={`relative h-full w-full max-w-md mx-auto bg-gray-900 ${isAnimating ? 'transition-transform duration-200' : ''}`}
                    onClick={handleOverlayToggle}
                    style={{ transform: `translateY(${-swipeOffset}px)` }}
                >
                    {/* Video Player */}
                    {isYoutubeUrl(currentShort?.video_url || '') ? (
                        <iframe
                            key={currentShort?.id}
                            src={getYoutubeEmbedUrl(currentShort?.video_url || '')}
                            className="absolute inset-0 w-full h-full pointer-events-none"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                        />
                    ) : (
                        <video
                            key={currentShort?.id}
                            src={currentShort?.video_url}
                            className="absolute inset-0 w-full h-full object-cover pointer-events-none"
                            loop
                            muted={muted}
                            playsInline
                            autoPlay
                        />
                    )}

                    {/* Gradient Overlays */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/30 pointer-events-none"></div>

                    {/* Close Button */}
                    <button
                        onClick={() => window.history.back()}
                        className="absolute top-4 left-4 p-2 bg-black/30 rounded-full hover:bg-black/50 transition z-20"
                    >
                        <X className="w-6 h-6" />
                    </button>

                    {/* Mute Toggle - Show for ALL videos */}
                    <button
                        onClick={() => setMuted(!muted)}
                        className="absolute top-4 right-4 p-2 bg-black/30 rounded-full hover:bg-black/50 transition z-20"
                    >
                        {muted ? <VolumeX className="w-6 h-6" /> : <Volume2 className="w-6 h-6" />}
                    </button>

                    {/* Video Info (Bottom Left) */}
                    <div className={`absolute bottom-28 left-4 right-20 z-20 transition-opacity duration-300 ${showOverlay ? 'opacity-100' : 'opacity-0'}`}>
                        <p className="font-bold text-lg mb-1">@phuquoc_explorer</p>
                        <p className="text-sm mb-2 line-clamp-2">{currentShort?.title || 'Video'}</p>
                        {currentShort?.description && (
                            <p className="text-xs text-gray-300 line-clamp-2">{currentShort.description}</p>
                        )}
                    </div>

                    {/* Linked Product Card (TikTok style) - Minimizes instead of hiding */}
                    {linkedItem && (
                        <Link
                            to={linkedItem.link}
                            className={`absolute left-4 z-20 transition-all duration-300 ${showOverlay
                                ? 'bottom-16 right-20 opacity-100'
                                : 'bottom-4 right-auto opacity-90'
                                }`}
                            onClick={(e) => e.stopPropagation()}
                        >
                            {showOverlay ? (
                                /* Expanded view - compact version */
                                <div className={`flex items-center gap-2 px-3 py-2 rounded-xl ${linkedItem.color} bg-opacity-90 backdrop-blur-sm`}>
                                    {/* Product Image - smaller */}
                                    {linkedItemDetails?.image ? (
                                        <div className="w-9 h-9 rounded-lg overflow-hidden flex-shrink-0">
                                            <img
                                                src={linkedItemDetails.image}
                                                alt={linkedItemDetails.name}
                                                className="w-full h-full object-cover"
                                                onError={(e) => { e.currentTarget.style.display = 'none' }}
                                            />
                                        </div>
                                    ) : (
                                        <div className="w-9 h-9 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                                            <linkedItem.icon className="w-5 h-5" />
                                        </div>
                                    )}
                                    {/* Product Info - compact */}
                                    <div className="flex-1 min-w-0">
                                        <p className="text-xs font-medium line-clamp-1">
                                            {linkedItemDetails?.name || linkedItem.label}
                                        </p>
                                        <p className="text-[10px] opacity-80 flex items-center gap-1">
                                            {linkedItemDetails?.price && <span className="font-bold">{linkedItemDetails.price}</span>}
                                            <span className="mx-1">•</span>
                                            <ShoppingBag className="w-2.5 h-2.5" />
                                            {language === 'en' ? 'Tap' : 'Chạm'}
                                        </p>
                                    </div>
                                    <ChevronUp className="w-4 h-4 rotate-90 flex-shrink-0 opacity-70" />
                                </div>
                            ) : (
                                /* Minimized view - small floating badge with marquee */
                                <div className={`flex items-center gap-2 px-3 py-2 rounded-full ${linkedItem.color} bg-opacity-90 backdrop-blur-sm shadow-lg`}>
                                    {linkedItemDetails?.image ? (
                                        <div className="w-6 h-6 rounded-full overflow-hidden flex-shrink-0">
                                            <img
                                                src={linkedItemDetails.image}
                                                alt=""
                                                className="w-full h-full object-cover"
                                            />
                                        </div>
                                    ) : (
                                        <linkedItem.icon className="w-4 h-4" />
                                    )}
                                    {/* Marquee Text Container */}
                                    <div className="overflow-hidden max-w-[140px]">
                                        <div className="animate-marquee whitespace-nowrap">
                                            <span className="text-xs font-medium">
                                                {linkedItemDetails?.name || linkedItem.label}
                                                {linkedItemDetails?.price && ` • ${linkedItemDetails.price}`}
                                            </span>
                                            <span className="mx-4 text-xs font-medium">
                                                {linkedItemDetails?.name || linkedItem.label}
                                                {linkedItemDetails?.price && ` • ${linkedItemDetails.price}`}
                                            </span>
                                        </div>
                                    </div>
                                    <ShoppingBag className="w-3 h-3 opacity-70 flex-shrink-0" />
                                </div>
                            )}
                        </Link>
                    )}

                    {/* Actions (Right Side) */}
                    <div className="absolute right-4 bottom-36 flex flex-col items-center gap-6 z-20">
                        <button
                            onClick={handleLike}
                            className="flex flex-col items-center group"
                        >
                            <Heart className={`w-8 h-8 transition ${liked.has(currentShort?.id) ? 'text-red-500 fill-red-500' : 'group-hover:text-red-500'}`} />
                            <span className="text-xs mt-1">{formatNumber(currentShort?.likes || 0)}</span>
                        </button>

                        <button
                            onClick={() => setShowComments(true)}
                            className="flex flex-col items-center"
                        >
                            <MessageCircle className="w-8 h-8" />
                            <span className="text-xs mt-1">{videoCommentsCount[currentShort?.id] || 0}</span>
                        </button>

                        <button
                            onClick={handleShare}
                            className="flex flex-col items-center"
                        >
                            {shareCopied ? (
                                <Check className="w-8 h-8 text-green-500" />
                            ) : (
                                <Share2 className="w-8 h-8" />
                            )}
                            <span className="text-xs mt-1">{shareCopied ? (language === 'en' ? 'Copied!' : 'Đã sao!') : 'Share'}</span>
                        </button>
                    </div>

                    {/* Navigation Arrows (Desktop Only - Left Side) */}
                    <div className={`absolute left-4 top-1/2 -translate-y-1/2 flex-col gap-3 z-20 hidden md:flex transition-opacity duration-300 ${showOverlay ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
                        <button
                            onClick={goPrev}
                            disabled={currentIndex === 0}
                            className={`p-3 bg-black/50 hover:bg-black/70 rounded-full transition ${currentIndex === 0 ? 'opacity-30 cursor-not-allowed' : ''}`}
                            title="Previous video (↑)"
                        >
                            <ChevronUp className="w-6 h-6" />
                        </button>
                        <button
                            onClick={goNext}
                            disabled={currentIndex === shorts.length - 1 && !hasMore}
                            className={`p-3 bg-black/50 hover:bg-black/70 rounded-full transition ${currentIndex === shorts.length - 1 && !hasMore ? 'opacity-30 cursor-not-allowed' : ''}`}
                            title="Next video (↓)"
                        >
                            <ChevronDown className="w-6 h-6" />
                        </button>
                    </div>

                    {/* Swipe Hint (Mobile) - Only show loading indicator */}
                    {loadingMore && (
                        <div className="absolute bottom-4 left-4 text-xs text-gray-400 z-20">
                            Loading more...
                        </div>
                    )}
                </div>
            </div>

            {/* Comments Modal */}
            {showComments && (
                <div className="fixed inset-0 bg-black/80 z-50 flex items-end justify-center">
                    <div className="bg-gray-900 w-full max-w-md h-[70vh] rounded-t-2xl flex flex-col">
                        <div className="flex items-center justify-between p-4 border-b border-gray-700">
                            <h3 className="font-bold">{language === 'en' ? 'Comments' : 'Bình luận'} ({comments.length})</h3>
                            <button onClick={() => setShowComments(false)}>
                                <X className="w-6 h-6" />
                            </button>
                        </div>

                        <div className="flex-1 overflow-y-auto p-4 space-y-4">
                            {comments.length === 0 ? (
                                <p className="text-center text-gray-500 py-8">
                                    {language === 'en' ? 'No comments yet' : 'Chưa có bình luận'}
                                </p>
                            ) : (
                                comments.map(comment => (
                                    <div key={comment.id} className="flex gap-3">
                                        <div className="w-8 h-8 rounded-full bg-gray-700 flex items-center justify-center text-sm font-bold">
                                            {comment.guest_name?.charAt(0).toUpperCase() || 'A'}
                                        </div>
                                        <div className="flex-1">
                                            <p className="text-sm font-medium">{comment.guest_name}</p>
                                            <p className="text-sm text-gray-300">{comment.content}</p>
                                            <p className="text-xs text-gray-500 mt-1">
                                                {new Date(comment.created_at).toLocaleDateString()}
                                            </p>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>

                        <form onSubmit={submitComment} className="p-4 border-t border-gray-700 flex gap-2">
                            <input
                                type="text"
                                value={commentText}
                                onChange={e => setCommentText(e.target.value)}
                                placeholder={language === 'en' ? 'Add a comment...' : 'Thêm bình luận...'}
                                className="flex-1 bg-gray-800 rounded-full px-4 py-2 text-sm outline-none"
                            />
                            <button
                                type="submit"
                                disabled={!commentText.trim() || submittingComment}
                                className="p-2 bg-primary-600 rounded-full disabled:opacity-50"
                            >
                                <Send className="w-5 h-5" />
                            </button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    )
}
